import { useEffect, useState } from 'react';
import { Modal, Spin, message, Button } from 'antd';
import { CloseOutlined, CheckOutlined, WarningOutlined, EditOutlined, SendOutlined } from '@ant-design/icons';
import { InspectionReport, inspectionReportService } from '@/services/inspectionReport.service';
import { inspectionReportReviewService } from '@/services/inspectionReportReview.service';
import { docmanService } from '@/services/docman.service';
import { FieldCorrection } from '@/shared/types/inspectionReportReview.types';
import { LaudoWizard } from './LaudoWizard';
import { AssignCalculationsModal } from './AssignCalculationsModal';
import { LaudoWizardData } from '../types/laudo-wizard.types';
import { InspectionRequest } from '@/services/types/inspection.types';
import { generatePdfBlobUrl, generatePdfFile } from '../pdf/utils/generatePdfOnTheFly';

interface ReviewReportModalProps {
  open: boolean;
  report: InspectionReport | null;
  reviewerType: 'Provider' | 'Bank';
  inspection: InspectionRequest;
  hasPreviousExternalReturn?: boolean;
  onClose: () => void;
  onReviewed?: (action: 'approved' | 'rejected' | 'edited') => void;
}

export function ReviewReportModal({
  open,
  report,
  reviewerType,
  inspection,
  hasPreviousExternalReturn,
  onClose,
  onReviewed,
}: ReviewReportModalProps) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showMarkerWizard, setShowMarkerWizard] = useState(false);
  const [showEditWizard, setShowEditWizard] = useState(false);
  const [wizardData, setWizardData] = useState<LaudoWizardData | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [showForwardModal, setShowForwardModal] = useState(false);

  const isAutomaticReport = (() => {
    if (inspection.items?.[0]?.isAutomaticReport === true) return true;
    if (!report?.reportData) return false;
    try {
      const parsed = JSON.parse(report.reportData);
      return parsed.isAutomaticReport === true;
    } catch {
      return false;
    }
  })();

  const loadData = async () => {
    if (!report || !report.reportData) return;

    setLoading(true);
    try {
      const reportDataParsed = JSON.parse(report.reportData);
      const data = reportDataParsed.wizardData;

      if (!data) {
        message.error('Dados do laudo não encontrados');
        return;
      }

      setWizardData(data);

      // Se o report já tem documento no bucket, busca a presigned URL.
      // Caso contrário (laudo aguardando aprovação do banco), gera o PDF on-the-fly.
      if (report.documentId) {
        const urlData = await inspectionReportService.getReportDownloadUrl(report.id);
        const presignedUrl = await docmanService.getImageUrl(urlData.documentId);
        setPdfUrl(presignedUrl);
      } else {
        const blobUrl = await generatePdfBlobUrl({
          wizardData: data,
          inspection,
          isAutomatic: isAutomaticReport,
        });
        setPdfUrl(blobUrl);
      }
    } catch {
      message.error('Erro ao carregar dados do laudo');
    } finally {
      setLoading(false);
    }
  };

  const buildPdfFile = async (data: LaudoWizardData): Promise<File> => {
    const filePrefix = isAutomaticReport ? 'laudo-automatico' : 'laudo';
    return generatePdfFile(
      { wizardData: data, inspection, isAutomatic: isAutomaticReport },
      `${filePrefix}-${inspection.proposalNumber}-${Date.now()}.pdf`,
    );
  };

  useEffect(() => {
    if (open && report) {
      loadData();
    } else {
      setShowMarkerWizard(false);
      setShowEditWizard(false);
      setWizardData(null);
      setPdfUrl((prev) => {
        if (prev && prev.startsWith('blob:')) {
          URL.revokeObjectURL(prev);
        }
        return null;
      });
    }
  }, [open, report]);

  const handleMarkProblems = () => {
    setShowMarkerWizard(true);
  };

  const handleEditDirectly = () => {
    setShowEditWizard(true);
  };

  const handleEditComplete = () => {
    message.success('Laudo atualizado com sucesso');
    onReviewed?.('edited');
    onClose();
  };

  const handleApproveWithoutCorrections = async () => {
    if (!report) return;

    setSubmitting(true);
    try {
      // Política: o PDF só vai ao bucket quando o BANCO aprovar.
      // Aprovação da Prestadora apenas repassa ao banco — não sobe nada.
      const shouldUploadToBucket = reviewerType === 'Bank' && !report.documentId && !!wizardData;

      if (shouldUploadToBucket) {
        const itemId = inspection.items?.[0]?.id;
        if (!itemId) {
          throw new Error('ID do item de inspeção não encontrado');
        }

        const pdfFile = await buildPdfFile(wizardData!);

        const uploadResult = await docmanService.uploadFile(
          itemId,
          pdfFile,
          'LAUDO_AVALIACAO',
        );

        await inspectionReportService.attachDocument(report.id, {
          documentId: uploadResult.documentId,
          fileName: pdfFile.name,
          fileSize: pdfFile.size,
          fileType: 'application/pdf',
          filePath: `docman/${uploadResult.documentId}`,
        });
      }

      await inspectionReportReviewService.approveReport(report.id, reviewerType, {
        generalComments: '',
      });

      message.success(
        reviewerType === 'Provider'
          ? 'Laudo aprovado e enviado para o banco'
          : 'Laudo aprovado com sucesso'
      );

      onReviewed?.('approved');
      onClose();
    } catch (err) {
      // Expõe o motivo (ex.: falha ao hidratar a foto de fachada na geração do
      // PDF) — a aprovação é bloqueada e o suporte consegue diagnosticar.
      const detalhe = err instanceof Error && err.message ? `: ${err.message}` : '';
      message.error(`Erro ao aprovar laudo${detalhe}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReviewComplete = async (corrections: FieldCorrection[], generalComments?: string) => {
    if (!report) return;

    try {
      setSubmitting(true);

      if (corrections.length === 0) {
        // Mesma política: só sobe ao bucket se for o BANCO aprovando e ainda não houver documento.
        const shouldUploadToBucket = reviewerType === 'Bank' && !report.documentId && !!wizardData;

        if (shouldUploadToBucket) {
          const itemId = inspection.items?.[0]?.id;
          if (!itemId) {
            throw new Error('ID do item de inspeção não encontrado');
          }

          const pdfFile = await buildPdfFile(wizardData!);

          const uploadResult = await docmanService.uploadFile(
            itemId,
            pdfFile,
            'LAUDO_AVALIACAO',
          );

          await inspectionReportService.attachDocument(report.id, {
            documentId: uploadResult.documentId,
            fileName: pdfFile.name,
            fileSize: pdfFile.size,
            fileType: 'application/pdf',
            filePath: `docman/${uploadResult.documentId}`,
          });
        }

        await inspectionReportReviewService.approveReport(report.id, reviewerType, {
          generalComments: generalComments || '',
        });

        message.success(
          reviewerType === 'Provider'
            ? 'Laudo aprovado e enviado para o banco'
            : 'Laudo aprovado com sucesso'
        );

        onReviewed?.('approved');
      } else {
        await inspectionReportReviewService.rejectReport(report.id, reviewerType, {
          generalComments: generalComments || '',
          fieldCorrections: corrections,
        });

        message.success(
          reviewerType === 'Provider'
            ? 'Laudo rejeitado. Correções enviadas ao inspetor.'
            : 'Laudo rejeitado. Correções enviadas à prestadora.'
        );

        onReviewed?.('rejected');
      }

      onClose();
    } catch (err) {
      const detalhe = err instanceof Error && err.message ? `: ${err.message}` : '';
      message.error(`Erro ao processar revisão${detalhe}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (showEditWizard && wizardData) {
    return (
      <Modal
        centered
        open={open}
        onCancel={onClose}
        width="98vw"
        style={{ maxWidth: '100%', top: 10 }}
        footer={null}
        closeIcon={<CloseOutlined />}
      >
        <LaudoWizard
          inspection={inspection}
          initialData={wizardData}
          editingReportId={report?.id}
          mode={isAutomaticReport ? 'editAutomatic' : 'edit'}
          onClose={(wasTransmitted) => {
            if (wasTransmitted) {
              handleEditComplete();
            } else {
              onClose();
            }
          }}
        />
      </Modal>
    );
  }

  if (showMarkerWizard && wizardData) {
    return (
      <Modal
        centered
        open={open}
        onCancel={onClose}
        width="98vw"
        style={{ maxWidth: '100%', top: 10 }}
        footer={null}
        closeIcon={<CloseOutlined />}
      >
        <LaudoWizard
          inspection={inspection}
          initialData={wizardData}
          mode="review"
          isAutomaticReport={isAutomaticReport}
          reviewContext={{
            reportId: report?.id || '',
            reviewerType,
          }}
          onClose={onClose}
          onReviewComplete={handleReviewComplete}
        />
      </Modal>
    );
  }

  return (
    <Modal
      centered
      open={open}
      onCancel={onClose}
      width="90vw"
      style={{ maxWidth: 1400 }}
      footer={
        !loading && pdfUrl && wizardData ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={handleApproveWithoutCorrections}
              loading={submitting}
            >
              Aprovar sem Correções
            </Button>
            {isAutomaticReport && reviewerType === 'Bank' && (
              <Button
                icon={<SendOutlined />}
                onClick={() => setShowForwardModal(true)}
              >
                Encaminhar para Prestadora
              </Button>
            )}
            {isAutomaticReport && reviewerType === 'Bank' ? (
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={handleEditDirectly}
                style={{ backgroundColor: '#fa8c16', borderColor: '#fa8c16' }}
              >
                Editar Laudo
              </Button>
            ) : (
              // Prestadora em ciclo pós-devolução do banco/SF não pode "Marcar Problemas"
              // (o inspetor só mexe no checklist; a prestadora deve editar o laudo direto
              // ou devolver ao inspetor pelo card de correção).
              !(reviewerType === 'Provider' && hasPreviousExternalReturn) && (
                <Button
                  type="default"
                  icon={<WarningOutlined />}
                  onClick={handleMarkProblems}
                  style={{ borderColor: '#ff4d4f', color: '#ff4d4f' }}
                >
                  Marcar Problemas
                </Button>
              )
            )}
          </div>
        ) : null
      }
      closeIcon={<CloseOutlined />}
    >
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Avaliar Laudo</h2>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 500 }}>
          <Spin size="large" tip="Carregando laudo..." />
        </div>
      ) : pdfUrl ? (
        <iframe
          src={pdfUrl}
          style={{
            width: '100%',
            height: '70vh',
            border: '1px solid #e8e8e8',
            borderRadius: 4,
          }}
          title="Visualização do Laudo"
        />
      ) : (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
          Não foi possível carregar o PDF
        </div>
      )}
      <AssignCalculationsModal
        open={showForwardModal}
        itemId={inspection.items?.[0]?.id || ''}
        onClose={() => setShowForwardModal(false)}
        onSuccess={() => {
          setShowForwardModal(false);
          onClose();
          onReviewed?.('forwarded' as any);
        }}
        title="Encaminhar Laudo Automático para Prestadora"
        description="Selecione a prestadora que receberá o laudo automático para revisão e complementação."
        successMessage="Laudo automático encaminhado para a prestadora com sucesso!"
        serviceMethod="forwardAutomaticToProvider"
      />
    </Modal>
  );
}
